// A read-only journal someone shared with a link (/share/<token>, share.js on the server):
// results, accounts, every trading day and its trades, and journal notes if they chose to share
// them. Nothing here can be changed.
import { Fragment, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from './api';
import { Logo } from './Brand';
import { accountStatus } from './balance';
import { performanceStats } from './stats';
import { formatMoney, netPnl, tradingDayKey, usd } from './pnl';
import { currentLocale } from './langState';
import { useI18n } from './i18n';

const tone = (n) => (n > 0 ? 'gain' : n < 0 ? 'loss' : '');
const dayText = (key) => new Date(`${key}T12:00:00`).toLocaleDateString(currentLocale(), { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
const time = (d) => new Date(d).toLocaleTimeString(currentLocale(), { hour: 'numeric', minute: '2-digit' });

export default function SharePage() {
  const { t } = useI18n();
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(null);
  const [shown, setShown] = useState(30);

  useEffect(() => {
    document.title = t('Shared trading journal | Market Matrix');
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    api(`/shared/${token}`).then(setData).catch((err) => setError(err.message));
    return () => meta.remove();
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const days = useMemo(() => {
    if (!data) return [];
    const byDay = {};
    for (const tr of data.trades) (byDay[tradingDayKey(tr.date)] = byDay[tradingDayKey(tr.date)] || []).push(tr);
    const notes = new Map((data.notes || []).map((n) => [n.date, n]));
    const keys = new Set([...Object.keys(byDay), ...(data.notesShared ? [...notes.keys()] : [])]);
    return [...keys].sort().reverse().map((d) => {
      const list = (byDay[d] || []).sort((a, b) => new Date(a.date) - new Date(b.date));
      return { day: d, trades: list, net: list.reduce((s, x) => s + netPnl(x), 0), note: notes.get(d) };
    });
  }, [data]);
  const stats = data ? performanceStats(data.trades) : null;
  const accountName = (id) => ((data && data.accounts.find((a) => String(a._id) === String(id))) || {}).name || '';

  return (
    <div className="share-page">
      <header className="share-top">
        <Link to="/" className="app-brand"><Logo /><span className="app-brand-text"><span className="brand-name">Market Matrix</span><span className="brand-tag">{t('Trading Journal')}</span></span></Link>
        <span className="share-badge">{t('Read-only')}</span>
      </header>
      {error && <p className="empty share-error">{error}</p>}
      {!data && !error && <p className="empty">{t('Loading…')}</p>}
      {data && (
        <main className="share-main">
          <p className="eyebrow">{t('Shared journal')}{data.label ? ` · ${data.label}` : ''}</p>
          <h1 className="routine-title">{t('{name}’s trading journal', { name: data.name })}</h1>
          <p className="subtitle">{t('{name} shared this so you can see how they trade. You can look around, but nothing can be changed.', { name: data.name })}</p>

          <section className="summary admin-summary">
            {[
              [t('Net P&L'), formatMoney(stats.net), tone(stats.net)],
              [t('Trades'), stats.count],
              [t('Win rate'), stats.winRate === null ? '—' : `${Math.round(stats.winRate)}%`],
              [t('Average win'), stats.avgWin === null ? '—' : usd.format(stats.avgWin)],
              [t('Average loss'), stats.avgLoss === null ? '—' : usd.format(stats.avgLoss)],
              [t('Profit factor'), stats.profitFactor === null ? '—' : stats.profitFactor === Infinity ? '∞' : stats.profitFactor.toFixed(2)],
            ].map(([label, value, cls]) => (
              <div key={label} className="stat"><span className="stat-label">{label}</span><span className={`stat-value ${cls || ''}`}>{value}</span></div>
            ))}
          </section>

          {data.accounts.filter((a) => !a.archived && !a.attemptOf).length > 0 && (
            <section className="insight-panel wide share-accounts-list">
              <h2>{t('Accounts')}</h2>
              <ul>
                {data.accounts.filter((a) => !a.archived && !a.attemptOf).map((a) => {
                  const s = accountStatus(a, data.trades.filter((x) => String(x.accountId) === String(a._id)));
                  return (
                    <li key={a._id}>
                      <strong>{a.name}</strong> <small>{t(a.type || 'Account')}</small>
                      {s && <span> &bull; {t('Balance')} {usd.format(s.balance)} &bull; <span className={tone(s.change)}>{formatMoney(s.change)}</span>{s.toBlow !== undefined && <> &bull; {t('{amount} above the max loss limit', { amount: usd.format(s.toBlow) })}</>}</span>}
                    </li>
                  );
                })}
              </ul>
            </section>
          )}

          <section className="insight-panel wide">
            <h2>{t('Trading days')}</h2>
            <p className="insight-panel-sub">{data.notesShared ? t('Newest first. Click a day to see its trades and journal notes.') : t('Newest first. Click a day to see its trades.')}</p>
            <div className="table-wrap">
              <table className="biz-table share-days">
                <thead><tr><th>{t('Day')}</th><th className="num">{t('Trades')}</th><th className="num">{t('Net P&L')}</th>{data.notesShared && <th>{t('Journal')}</th>}</tr></thead>
                <tbody>
                  {days.slice(0, shown).map((d) => (
                    <Fragment key={d.day}>
                      <tr className="share-day" onClick={() => setOpen(open === d.day ? null : d.day)}>
                        <th scope="row"><button type="button" className="link-button" aria-expanded={open === d.day}>{dayText(d.day)}</button></th>
                        <td className="num">{d.trades.length}</td>
                        <td className={`num ${tone(d.net)}`}>{d.trades.length ? formatMoney(d.net) : '—'}</td>
                        {data.notesShared && <td>{d.note && (d.note.plan || d.note.review) ? '✓' : ''}{d.note && d.note.emotionsHit && d.note.emotionsHit.length ? ` ${d.note.emotionsHit.join(', ')}` : ''}</td>}
                      </tr>
                      {open === d.day && (
                        <tr className="share-detail"><td colSpan={data.notesShared ? 4 : 3}>
                          {d.note && (d.note.plan || d.note.review || d.note.workOn) && (
                            <div className="share-notes">
                              {d.note.plan && <p><strong>{t('Plan')}:</strong> {d.note.plan}</p>}
                              {d.note.review && <p><strong>{t('Review')}:</strong> {d.note.review}</p>}
                              {d.note.workOn && <p><strong>{t('Work on')}:</strong> {d.note.workOn}</p>}
                            </div>
                          )}
                          {d.trades.length > 0 && (
                            <table className="biz-table">
                              <thead><tr><th>{t('Time')}</th><th>{t('Symbol')}</th><th>{t('Side')}</th><th className="num">{t('Size')}</th><th className="num">{t('Entry')}</th><th className="num">{t('Exit')}</th><th className="num">{t('Net P&L')}</th><th>{t('Account')}</th></tr></thead>
                              <tbody>
                                {d.trades.map((x) => (
                                  <Fragment key={x._id}>
                                    <tr>
                                      <td>{x.timeUnknown ? '—' : time(x.date)}</td><td>{x.symbol}</td><td>{t(x.side || 'Long')}</td><td className="num">{x.size}</td>
                                      <td className="num">{x.entryPrice}</td><td className="num">{x.exitPrice}</td><td className={`num ${tone(netPnl(x))}`}>{formatMoney(netPnl(x))}</td><td>{accountName(x.accountId)}</td>
                                    </tr>
                                    {(x.notes || (x.tags && x.tags.length) || x.grade) && (
                                      <tr className="share-trade-note"><td colSpan={8}>{[x.grade, (x.tags || []).join(', '), x.notes].filter(Boolean).join(' · ')}</td></tr>
                                    )}
                                  </Fragment>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </td></tr>
                      )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
            {days.length > shown && <button type="button" className="secondary small" onClick={() => setShown((n) => n + 30)}>{t('Show more days')}</button>}
          </section>
          <p className="field-hint share-foot">{t('Shared from Market Matrix, the trading journal for prop traders.')} <Link to="/">{t('Start your own journal')} &rarr;</Link></p>
        </main>
      )}
    </div>
  );
}
